'use client';
import { useEffect, useRef, useState } from 'react';
import { bundles, categories, ETSY_URL } from '@/data/bundles';

const catClassMap: Record<string, string> = {
  'Productivity': 'cat-productivity',
  'Design & Media': 'cat-design',
  'Tech & Dev': 'cat-tech',
  'Education': 'cat-education',
  'Content Creation': 'cat-content',
  'Business': 'cat-business',
};

export default function CatalogSection() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const headingRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-hidden');
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (headingRef.current) observer.observe(headingRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, []);

  const filtered = bundles.filter((b) => {
    const matchCat = activeCategory === 'All' || b.category === activeCategory;
    const matchSearch = b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.badge.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <section id="bundles" className="relative py-20 lg:py-24 bg-section-dark" aria-labelledby="catalog-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 reveal-hidden" ref={headingRef}>
          <h2 id="catalog-heading" className="section-heading mb-4" style={{ color: 'var(--foreground)' }}>
            43 Premium Collections.{' '}
            <span className="text-gradient">Explore Everything Inside.</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            Browse the complete creator, business, publishing, development and design library.
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-2xl mx-auto">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={20} height={20}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
          </div>
          <input
            type="search"
            className="search-input"
            placeholder="Search templates, photos, SVGs, apps, planners..."
            aria-label="Search bundle collections"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Category Filters */}
        <div id="categories" className="flex flex-wrap gap-2 justify-center mb-8" role="group" aria-label="Filter by category">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`filter-btn ${activeCategory === cat ? 'active' : ''}`}
              aria-pressed={activeCategory === cat}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Count */}
        <div className="text-center mb-8">
          <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{filtered.length} collections found</span>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((bundle, i) => (
            <div
              key={bundle.id}
              className="product-card group"
            >
              <div className="card-img-wrapper">
                <img
                  src={bundle.image}
                  alt={bundle.alt}
                  loading="lazy"
                  width={600}
                  height={338}
                  className="w-full h-full object-cover bg-gray-800"
                />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`category-pill ${catClassMap[bundle.category] || 'cat-productivity'}`}>{bundle.category}</span>
                  <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>{bundle.badge}</span>
                </div>
                <h3 className="font-semibold text-sm leading-snug line-clamp-2 mb-3" style={{ color: 'var(--foreground)' }}>
                  {bundle.title}
                </h3>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold" style={{ color: 'var(--secondary-foreground)' }}>✓ Included in Bundle</span>
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>$79 total</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-14 reveal-hidden" ref={ctaRef}>
          <p className="mb-4 text-lg font-medium" style={{ color: 'var(--muted-foreground)' }}>All 43 collections. One purchase. $79.</p>
          <a href={ETSY_URL} target="_blank" rel="noopener noreferrer" className="btn-primary text-base py-4 px-8 inline-flex">
            Get the Complete Bundle — $79
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={18} height={18}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
